import { useState } from "react";
import { Button, TextField } from "@mui/material";
import { ApiRequest } from "GlobalFunctions";
import { PiCaretRightBold, PiCheckCircle } from "react-icons/pi";
import { enqueueSnackbar } from "notistack";


interface UpdatePaymentMethodProps {
    subscriptionData: any;
    onGoBack: () => void;
    onUpdateSuccess: () => void;
}

export default function UpdatePaymentMethod(props: UpdatePaymentMethodProps) {
    const { subscriptionData, onGoBack, onUpdateSuccess } = props;
    const [loading, setLoading] = useState(false);
    const [updated, setUpdated] = useState(false);
    const [errors, setErrors] = useState<any>({
        card_expiration: false,
    });

    const defaultValues = {
        name_on_card: subscriptionData?.name_on_card || '',
        card_number: '',
        card_expiration: '',
        cvv: '',
    };

    const [formValues, setFormValues] = useState(defaultValues);

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'long', 
            day: 'numeric', 
            year: 'numeric',
            timeZone: 'UTC'
        });
    };

    /*=======================================
    FORMAT FUNCTIONS
    =======================================*/
    const formatCardNumber = (value: string) => {
        return value
            .replace(/\D/g, '')
            .replace(/(.{4})/g, '$1 ')
            .trim()
            .substring(0, 19);
    };

    const formatExpirationDate = (value: string) => {
        let cleanedValue = value.replace(/\D/g, '');
        if (cleanedValue.length > 2) {
            cleanedValue = cleanedValue.substring(0, 2) + '/' + cleanedValue.substring(2);
        }
        return cleanedValue.substring(0, 5);
    };

    /*=======================================
    VALIDATION FUNCTIONS
    =======================================*/
    const validateExpirationDate = (value: string) => {
        const [month, year] = value.split('/').map(Number);
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear() % 100;

        let isValid = true;

        if (month > 12 || month < 1) {
            isValid = false;
        }

        if (year < currentYear || (year === currentYear && month < currentMonth)) {
            isValid = false;
        }

        setErrors({ ...errors, card_expiration: !isValid });
    };

    /*=======================================
    HANDLE INPUT CHANGE
    =======================================*/
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        let formattedValue = value;
        if (name === 'card_number') {
            formattedValue = formatCardNumber(value);
        } else if (name === 'card_expiration') {
            formattedValue = formatExpirationDate(value);
            validateExpirationDate(formattedValue);
        }

        setFormValues({
            ...formValues,
            [name]: formattedValue,
        });
    };

    const submitUpdatePaymentMethod = () => {
        // Validation
        if (!formValues.name_on_card || formValues.name_on_card.length < 2) {
            enqueueSnackbar('Please enter a valid name', { variant: 'error' });
            return;
        }

        if (!formValues.card_number || formValues.card_number.length < 19) {
            enqueueSnackbar('Please enter a valid card number', { variant: 'error' });
            return;
        }

        if (errors.card_expiration || !formValues.card_expiration) {
            enqueueSnackbar('Please enter a valid expiration date', { variant: 'error' });
            return;
        }

        if (!formValues.cvv || formValues.cvv.length < 3) {
            enqueueSnackbar('Please enter a valid CVV', { variant: 'error' });
            return;
        }

        setLoading(true);
        ApiRequest({
            url: '/payments/subscription/updateCreditCard',
            method: 'POST',
            body: {
                subscription: {
                    subscription_id: subscriptionData?.subscription_id
                },
                store_id: subscriptionData?.store_id,
                name_on_card: formValues.name_on_card,
                card_number: formValues.card_number,
                card_expiration: formValues.card_expiration,
                cvv: formValues.cvv,
            },
            setResponse: (response: any) => {
                setLoading(false);
                if (response.status === "success") {
                    setUpdated(true);
                } else {
                    enqueueSnackbar(response.message || "Failed to update payment method", { variant: 'error' });
                }
            }
        });
    };

    return (
        <div className="pc-subscription-section">
            <div className="pc-cancel-breadcrumb">
                <span>BILLING</span>
                <PiCaretRightBold />
                <span>UPDATE CARD</span>
            </div>

            {updated ? (
                <div className="pc-cancel-success-overlay">
                    <PiCheckCircle className="pc-cancel-success-icon" />
                        <h1 className="pc-cancel-success-title">Card Update Successful</h1>
                        <p className="pc-cancel-success-message">Updates to your payment method have been made.</p>
                    <Button
                        variant="contained"
                        onClick={onUpdateSuccess}
                        fullWidth
                        style={{
                            backgroundColor: subscriptionData?.primary_color || '#303030',
                            color: '#fff',
                            borderRadius: '10px',
                            padding: '10px 40px',
                            fontSize: '16px',
                            textTransform: 'none',
                            marginTop: '30px',
                            maxWidth: '400px'
                        }}
                    >
                        Back To Account
                    </Button>
                </div>
            ) : (
                <>
                    <h1 className="pc-cancel-title">Update Payment Method</h1>

                    

                    <div className="pc-update-form">
                        <div className="pc-form-row">
                            <div className="pc-form-field pc-form-field-full">
                                <label>Name on Card</label>
                                <TextField
                                    name="name_on_card"
                                    type="text"
                                    size="small"
                                    fullWidth
                                    value={formValues.name_on_card}
                                    onChange={handleInputChange}
                                    placeholder="John Doe"
                                />
                            </div>
                        </div>

                        <div className="pc-form-row">
                            <div className="pc-form-field pc-form-field-full">
                                <label>Card Number</label>
                                <TextField
                                    name="card_number"
                                    type="text"
                                    size="small"
                                    fullWidth
                                    value={formValues.card_number}
                                    onChange={handleInputChange}
                                    inputProps={{ maxLength: 19 }}
                                    placeholder="1234 5678 9012 3456"
                                />
                            </div>
                        </div>

                        <div className="pc-form-row">
                            <div className="pc-form-field">
                                <label>Expiration Date</label>
                                <TextField
                                    name="card_expiration"
                                    type="text"
                                    size="small"
                                    fullWidth
                                    value={formValues.card_expiration}
                                    onChange={handleInputChange}
                                    error={errors.card_expiration}
                                    helperText={errors.card_expiration ? "Invalid date MM/YY" : ""}
                                    placeholder="MM/YY"
                                />
                            </div>
                            <div className="pc-form-field">
                                <label>CVV</label>
                                <TextField
                                    name="cvv"
                                    type="text"
                                    size="small"
                                    fullWidth
                                    value={formValues.cvv}
                                    onChange={handleInputChange}
                                    inputProps={{ maxLength: 4 }}
                                    placeholder="123"
                                />
                            </div>
                        </div>


                        

                        <div className="pc-cancel-actions">

                            <p className="pc-update-note">
                                You can update your payment info until {formatDate(subscriptionData?.latest_next_payment_date)}.
                            </p>

                            <Button
                                variant="contained"
                                onClick={submitUpdatePaymentMethod}
                                disabled={loading}
                                fullWidth
                                style={{
                                    backgroundColor: subscriptionData?.primary_color || '#303030',
                                    color: '#fff',
                                    borderRadius: '10px',
                                    padding: '10px 40px',
                                    fontSize: '16px',
                                    textTransform: 'none',
                                    width: '100%',
                                }}
                            >
                                Update
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={onGoBack}
                                fullWidth
                                style={{
                                    borderColor: subscriptionData?.primary_color || '#303030',
                                    color: subscriptionData?.primary_color || '#303030',
                                    borderRadius: '10px',
                                    padding: '10px 40px',
                                    fontSize: '16px',
                                    textTransform: 'none',
                                    width: '100%',
                                    marginTop: '15px'
                                }}
                            >
                                Go back
                            </Button>
                        </div>
                    </div>

                    
                </>
            )}
        </div>
    );
}
